import {
  Subscription,
  SubscriptionSummary,
} from '../../../../../types/subscription';
import { useSelector } from 'react-redux';
import { selectSubscriptionSummaries } from '../../../../../store/slices/subscription';
import { useMemo, useState } from 'react';
import { colors } from '../../../../../colors';
import { formatNumber } from '../../../../../helpers/NumberFormatter';
import AssetIcon from '../../../../../components/AssetIcon';
import {
  useRemoveSubscriptionMutation,
  useUpdateSubscriptionStateMutation,
} from '../../../../../store/services/api';
import Checkbox from '../../../../../components/Checkbox';
import { selectUserId } from '../../../../../store/slices/user';

interface Props {
  subscription: Subscription;
  maxDecimals: number | undefined;
}

/**
 * @TODO
 * We don't need subscription summaries in separate array
 * Make sure balance gets instantly updated,
 * Show succes or failed response */

const SubscriptionStates = ({
  activeSubscriptionId,
  maxDecimals,
}: {
  activeSubscriptionId: number;
  maxDecimals: number | undefined;
}) => {
  const userId = useSelector(selectUserId);
  const [updateSubscriptionState] = useUpdateSubscriptionStateMutation();
  const allSubscriptions = useSelector(selectSubscriptionSummaries);

  const items = useMemo(() => {
    const activeSubscription = allSubscriptions.find(
      (s) => s.id === activeSubscriptionId,
    );
    return [activeSubscription!];
  }, [activeSubscriptionId, allSubscriptions]);

  const handleItemPress = async (
    subscriptionId: number,
    updatedValue: boolean,
  ) => {
    await updateSubscriptionState({
      userId,
      subscriptionId,
      isActive: updatedValue,
    });
  };

  return (
    <div
      style={{
        paddingLeft: 16,
        paddingRight: 16,
        marginTop: -5,
        paddingBottom: 32,
        borderBottom: '0.5px solid rgba(80, 80, 80, 0.3)',
      }}
    >
      <p
        style={{
          color: 'white',
          fontWeight: 'bold',
          fontSize: 20,
          marginBottom: 5,
        }}
      >
        Subscription State
      </p>
      <span style={{ color: colors.orange }}>
        When you pause a subscription, the program does not touch the assigned
        balances. This also means if you pause a subscription when it had
        bought, you are at risk of a downwards market.
      </span>
      <div
        style={{
          display: 'grid',
          gap: 16,
          marginTop: 20,
          justifyContent: 'center',
          gridTemplateColumns: 'repeat(3, 1fr)',
        }}
      >
        {items.map((item) => (
          <StateItem
            key={item.id}
            item={item}
            maxDecimals={maxDecimals}
            handleItemPress={handleItemPress}
          />
        ))}
      </div>
    </div>
  );
};

const StateItem = ({
  item,
  maxDecimals,
  handleItemPress,
}: {
  item: SubscriptionSummary;
  maxDecimals: number | undefined;
  handleItemPress: (
    subscriptionId: number,
    updatedValue: boolean,
  ) => Promise<void>;
}) => {
  return (
    <div
      style={{
        border: '1px solid gray',
        borderRadius: 8,
        boxShadow: '4px 0px 5px rgba(255, 255, 255, 0.2)',
        padding: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <div>
        <div
          style={{
            color: colors.white,
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <AssetIcon baseAsset={item.baseAsset} width={20} height={20} />
          {item.baseAsset} / {item.quoteAsset}
        </div>
        <p style={{ color: colors.white, margin: 0 }}>
          <span style={{ color: colors.gray }}>{item.baseAsset}</span>{' '}
          {formatNumber(item.assetBalance, maxDecimals)}
        </p>
        <p style={{ color: colors.white, margin: 0 }}>
          <span style={{ color: colors.gray }}>{item.quoteAsset}</span>{' '}
          {formatNumber(item.stableBalance, 2)}
        </p>
      </div>
      <div
        style={{
          height: '90%',
          aspectRatio: 1,
          borderRadius: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: !item.isActive
            ? `${colors.green}5A`
            : `${colors.red}5A`,
          border: !item.isActive
            ? `2px solid ${colors.green}9A`
            : `2px solid ${colors.red}9A`,
          cursor: 'pointer',
        }}
        onClick={() => handleItemPress(item.id, !item.isActive)}
      >
        <span style={{ color: colors.white, fontWeight: 'bold', fontSize: 14 }}>
          {item.isActive ? 'Pause' : 'Resume'}
        </span>
      </div>
    </div>
  );
};

const RemoveSubsription = ({
  subscription,
}: {
  subscription: SubscriptionSummary;
}) => {
  const userId = useSelector(selectUserId);
  const [removeSubsription] = useRemoveSubscriptionMutation();
  const [shouldSellAllAssets, setShouldSellAllAssets] = useState(false);

  const canSellBaseAsset = !!subscription.assetBalance;

  const handleSubmit = async () => {
    if (!userId) {
      return;
    }

    await removeSubsription({
      userId,
      subscriptionId: subscription.id,
      shouldSell: shouldSellAllAssets,
    });
  };

  return (
    <div
      style={{
        paddingLeft: 16,
        paddingRight: 16,
        marginTop: -5,
        borderBottom: '0.5px solid rgba(80, 80, 80, 0.3)',
        paddingBottom: 32,
      }}
    >
      <p
        style={{
          color: 'white',
          fontWeight: 'bold',
          fontSize: 20,
          marginBottom: 5,
        }}
      >
        Remove Subscription
      </p>

      {canSellBaseAsset && (
        <p style={{ color: colors.orange }}>
          <Checkbox
            label={`By selecting this checkbox, you are telling the program to sell all remaining assets the bot holds. In this case, that will be ${subscription.assetBalance} ${subscription.baseAsset}.`}
            color={colors.orange}
            value={shouldSellAllAssets}
            handleValueChange={setShouldSellAllAssets}
          />
        </p>
      )}

      <button
        style={{
          backgroundColor: colors.red,
          paddingLeft: 16,
          paddingRight: 16,
          paddingTop: 8,
          paddingBottom: 8,
          color: colors.white,
          borderRadius: 4,
          fontWeight: 800,
          border: 0,
          cursor: 'pointer',
        }}
        onClick={handleSubmit}
      >
        Remove
      </button>
    </div>
  );
};

const ManageState = (props: Props) => {
  return (
    <>
      <SubscriptionStates
        activeSubscriptionId={props.subscription.id}
        maxDecimals={props.maxDecimals}
      />
      <RemoveSubsription subscription={props.subscription} />
    </>
  );
};

export default ManageState;
